/**
 * DESIGN TOKENS — Single Source of Truth
 * 
 * All design values live here. Import this file in:
 * - themes.tsx (MUI theme)
 * - Any component needing direct token access
 * 
 * CSS variables are generated from these values in design-tokens.css
 * When updating values here, also update design-tokens.css to match.
 */

export const tokens = {
    // ============================================================
    // COLORS
    // ============================================================
    colors: {
        // Background gradient
        bgStart: '#f8f8f7',
        bgMid: '#f0eeeb',
        bgEnd: '#e8e4df',

        // Surfaces
        surface: '#f8f8f7',
        surfaceSubtle: '#f2f1ef',
        surfaceMuted: '#e8e6e3',
        hover: 'rgba(0, 0, 0, 0.04)',

        // Text hierarchy
        text: '#1a1a1a',
        textSecondary: '#6b6b6b',
        textMuted: '#999999',
        textInverse: '#ffffff',

        // Accent — Refined gold
        accent: '#b8860b',
        accentHover: '#9a7209',
        accentSoft: 'rgba(184, 134, 11, 0.1)',

        // Secondary — Sage green
        sage: '#4a5d4a',
        sageLight: 'rgba(100, 140, 120, 0.35)',

        // Aqua tint
        aqua: 'rgba(200, 230, 240, 0.25)',
        aquaLight: 'rgba(180, 210, 230, 0.15)',

        // Semantic
        error: '#c41e3a',
        success: '#4a5d4a',
    },

    // ============================================================
    // DARK MODE COLORS
    // ============================================================
    darkColors: {
        // Background (from hair check screen)
        bgStart: '#1c2536',
        bgMid: '#232d40',
        bgEnd: '#2a3548',

        // Surfaces
        surface: '#2f3b52',
        surfaceSubtle: '#384459',
        surfaceMuted: '#1a2030',
        hover: 'rgba(255, 255, 255, 0.08)',

        // Text hierarchy
        text: '#ffffff',
        textSecondary: '#b0b8c8',
        textMuted: '#7a8494',
        textInverse: '#1a1a1a',

        // Glass effects for dark mode
        glassWhite: 'rgba(255, 255, 255, 0.08)',
        glassWhiteSubtle: 'rgba(255, 255, 255, 0.04)',
        glassWhiteStrong: 'rgba(255, 255, 255, 0.12)',
        glassBorder: 'rgba(255, 255, 255, 0.1)',
        glassBorderLight: 'rgba(255, 255, 255, 0.15)',

        glassSurface1: 'rgba(255, 255, 255, 0.12)',
        glassSurface2: 'rgba(255, 255, 255, 0.06)',
        glassSurface3: 'rgba(255, 255, 255, 0.1)',

        glassDialogBg: 'rgba(28, 37, 54, 0.85)',

        // Secondary — Sage green (darker for readability with white text)
        sage: '#3a4d3a',
        sageLight: '#4a5d4a',
    },

    // ============================================================
    // GLASS EFFECTS
    // ============================================================
    glass: {
        blur: '24px',
        blurStrong: '40px',
        saturate: '200%',

        // Backgrounds
        white: 'rgba(255, 255, 255, 0.5)',
        whiteSubtle: 'rgba(255, 255, 255, 0.2)',
        whiteStrong: 'rgba(255, 255, 255, 0.7)',

        surface1: 'rgba(255, 255, 255, 0.6)',
        surface2: 'rgba(255, 255, 255, 0.35)',
        surface3: 'rgba(255, 255, 255, 0.5)',

        dialogBg: 'rgba(255, 255, 255, 0.9)',

        // Borders
        border: 'rgba(255, 255, 255, 0.6)',
        borderLight: 'rgba(255, 255, 255, 0.8)',
        borderSubtle: 'rgba(0, 0, 0, 0.06)',
    },

    // ============================================================
    // SHADOWS
    // ============================================================
    shadows: {
        sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
        md: '0 4px 12px rgba(0, 0, 0, 0.06)',
        lg: '0 8px 24px rgba(0, 0, 0, 0.08)',
        xl: '0 8px 32px rgba(0, 0, 0, 0.1)',
        glass: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 2px 6px rgba(255, 255, 255, 0.7),
      inset 0 -2px 4px rgba(0, 0, 0, 0.03)
    `.trim(),
    },

    // ============================================================
    // TYPOGRAPHY
    // ============================================================
    typography: {
        fontSans: "'Newsreader', Georgia, serif",
        // fontSans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSerif: "'Newsreader', Georgia, serif",

        // Sizes
        textXs: '0.75rem',
        textSm: '0.875rem',
        textBase: '0.925rem',
        textLg: '1rem',
        textXl: '1.25rem',
        text2xl: '1.5rem',
        text3xl: '2rem',

        // Weights
        fontNormal: 400,
        fontMedium: 500,
        fontSemibold: 600,
    },

    // ============================================================
    // SPACING
    // ============================================================
    spacing: {
        xs: '0.25rem',  // 4px
        sm: '0.5rem',   // 8px
        md: '1rem',     // 16px
        lg: '1.5rem',   // 24px
        xl: '2.5rem',   // 40px
        '2xl': '4rem',  // 64px
    },

    // ============================================================
    // BORDERS
    // ============================================================
    radius: {
        sm: '0.5rem',   // 8px
        md: '1rem',     // 16px — also used as MUI borderRadius (12)
        lg: '1.25rem',  // 20px
        xl: '1.75rem',  // 28px
        full: '9999px',
    },

    // MUI uses numeric pixels for borderRadius
    radiusPx: {
        sm: 8,
        md: 12,
        lg: 16,
        xl: 20,
    },

    // ============================================================
    // TRANSITIONS
    // ============================================================
    transitions: {
        fast: '150ms ease',
        base: '200ms ease',
        smooth: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
} as const;

// Type for the tokens object
export type DesignTokens = typeof tokens;
